import { v4 as uuidv4 } from 'uuid';

export type StoredSite = {
    id: string;
    email: string;
    password: string;
    restaurantName?: string;
    addedAt: number;
};

type StoredSitesData = {
    sites: StoredSite[];
    version: number;
};

const isStoredSite = (value: unknown): value is StoredSite => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const obj = value as Record<string, unknown>;
    return (
        typeof obj.id === 'string' &&
        typeof obj.email === 'string' &&
        typeof obj.password === 'string' &&
        typeof obj.addedAt === 'number' &&
        (obj.restaurantName === undefined || typeof obj.restaurantName === 'string')
    );
};

const isStoredSitesData = (value: unknown): value is StoredSitesData => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const obj = value as Record<string, unknown>;
    return (
        typeof obj.version === 'number' &&
        Array.isArray(obj.sites) &&
        obj.sites.every(isStoredSite)
    );
};

const STORAGE_KEY = 'stored_sites';
const OBFUSCATION_KEY = 'backresto_site_v1';
const CURRENT_VERSION = 1;

const createEmptyData = (): StoredSitesData => ({
    sites: [],
    version: CURRENT_VERSION
});

const obfuscate = (text: string): string => {
    const xored = text
        .split('')
        .map((char, i) =>
            String.fromCharCode(
                char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
            )
        )
        .join('');
    return btoa(xored);
};

const deobfuscate = (encoded: string): string => {
    const xored = atob(encoded);
    return xored
        .split('')
        .map((char, i) =>
            String.fromCharCode(
                char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
            )
        )
        .join('');
};

const getStoredData = (): StoredSitesData => {
    if (typeof window === 'undefined') {
        return createEmptyData();
    }

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return createEmptyData();
        }

        const decoded = deobfuscate(raw);
        const parsed = JSON.parse(decoded);

        if (!isStoredSitesData(parsed)) {
            return createEmptyData();
        }

        return parsed;
    } catch {
        return createEmptyData();
    }
};

const saveStoredData = (data: StoredSitesData): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const json = JSON.stringify(data);
        const encoded = obfuscate(json);
        localStorage.setItem(STORAGE_KEY, encoded);
    } catch {
        // Silently fail - storage operations are best-effort
    }
};

export const getStoredSites = (): StoredSite[] => {
    return getStoredData().sites;
};

type AddSiteOptions = {
    email: string;
    password: string;
    restaurantName?: string;
};

export const addStoredSite = (options: AddSiteOptions): StoredSite => {
    const { email, password, restaurantName } = options;
    const data = getStoredData();

    const existingIndex = data.sites.findIndex(
        (site) => site.email.toLowerCase() === email.toLowerCase()
    );

    const newSite: StoredSite = {
        id: existingIndex >= 0 ? data.sites[existingIndex].id : uuidv4(),
        email: email.toLowerCase(),
        password: obfuscate(password),
        restaurantName,
        addedAt: Date.now()
    };

    const updatedSites =
        existingIndex >= 0
            ? data.sites.map((site, index) => (index === existingIndex ? newSite : site))
            : [...data.sites, newSite];

    saveStoredData({ ...data, sites: updatedSites });
    return newSite;
};

export const removeStoredSite = (siteId: string): void => {
    const data = getStoredData();
    const updatedSites = data.sites.filter((site) => site.id !== siteId);
    saveStoredData({ ...data, sites: updatedSites });
};

type UpdateSiteOptions = {
    siteId: string;
    email?: string;
    password?: string;
    restaurantName?: string;
};

export const updateStoredSite = (options: UpdateSiteOptions): StoredSite | undefined => {
    const { siteId, email, password, restaurantName } = options;
    const data = getStoredData();
    const existingSite = data.sites.find((site) => site.id === siteId);

    if (!existingSite) {
        return undefined;
    }

    const updatedSite: StoredSite = {
        ...existingSite,
        email: email ? email.toLowerCase() : existingSite.email,
        password: password ? obfuscate(password) : existingSite.password,
        restaurantName: restaurantName ?? existingSite.restaurantName
    };

    const updatedSites = data.sites.map((site) => (site.id === siteId ? updatedSite : site));

    saveStoredData({ ...data, sites: updatedSites });
    return updatedSite;
};

export const getSiteCredentials = (
    siteId: string
): { email: string; password: string } | undefined => {
    const data = getStoredData();
    const site = data.sites.find((s) => s.id === siteId);

    if (!site) {
        return undefined;
    }

    return {
        email: site.email,
        password: deobfuscate(site.password)
    };
};

export const hasStoredSites = (): boolean => {
    return getStoredSites().length > 0;
};

export const updateSiteRestaurantName = (options: {
    email: string;
    restaurantName: string;
}): StoredSite | undefined => {
    const { email, restaurantName } = options;
    const data = getStoredData();
    const existingSite = data.sites.find(
        (site) => site.email.toLowerCase() === email.toLowerCase()
    );

    if (!existingSite) {
        return undefined;
    }

    const updatedSite: StoredSite = {
        ...existingSite,
        restaurantName
    };

    const updatedSites = data.sites.map((site) =>
        site.email.toLowerCase() === email.toLowerCase() ? updatedSite : site
    );

    saveStoredData({ ...data, sites: updatedSites });
    return updatedSite;
};
